import { apiFetchJson } from '../http/apiFetch';
import type {
  AdminCompanyDetail,
  AdminCompanyDirectoryRow,
  AdminCompanyInvitation,
  AdminCompanyMerchant,
  AdminCompanyStatus,
  AdminCompanyUser,
} from '../../models/adminCompany';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function normalizeMerchant(row: unknown): AdminCompanyMerchant | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const m = row as Record<string, unknown>;
  const merchantId = asString(m.merchantId ?? m.MerchantId).trim();
  if (!merchantId) {
    return null;
  }
  return {
    merchantId,
    name: asString(m.name ?? m.Name).trim(),
    email: asString(m.email ?? m.Email).trim(),
    status: asString(m.status ?? m.Status ?? 'Pending').trim(),
    userId: asOptionalString(m.userId ?? m.UserId),
    banned: Boolean(m.banned ?? m.Banned),
  };
}

function normalizeCompanyUser(row: unknown): AdminCompanyUser | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const u = row as Record<string, unknown>;
  const id = asString(u.id ?? u.Id).trim();
  if (!id) {
    return null;
  }
  const rawRoles = u.roles ?? u.Roles;
  const roles = Array.isArray(rawRoles) ? rawRoles.map((r) => asString(r).trim()).filter(Boolean) : [];
  return {
    id,
    email: asString(u.email ?? u.Email).trim(),
    firstName: asString(u.firstName ?? u.FirstName).trim(),
    lastName: asString(u.lastName ?? u.LastName).trim(),
    roles,
    banned: Boolean(u.banned ?? u.Banned),
  };
}

function normalizeInvitation(row: unknown): AdminCompanyInvitation | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const i = row as Record<string, unknown>;
  const id = asString(i.id ?? i.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    email: asString(i.email ?? i.Email).trim(),
    suggestedMerchantName: asString(i.suggestedMerchantName ?? i.SuggestedMerchantName).trim(),
    status: asString(i.status ?? i.Status ?? 'Pending').trim(),
    createdAt: asString(i.createdAt ?? i.CreatedAt),
    invitationExpiresAt: asOptionalString(i.invitationExpiresAt ?? i.InvitationExpiresAt),
  };
}

function normalizeCompanyDetail(row: unknown): AdminCompanyDetail | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const c = row as Record<string, unknown>;
  const companyId = asString(c.companyId ?? c.CompanyId).trim();
  if (!companyId) {
    return null;
  }
  const rawMerchants = c.merchants ?? c.Merchants;
  const merchants = Array.isArray(rawMerchants)
    ? rawMerchants.map(normalizeMerchant).filter((m): m is AdminCompanyMerchant => Boolean(m))
    : [];
  const rawInvitations = c.pendingInvitations ?? c.PendingInvitations;
  const pendingInvitations = Array.isArray(rawInvitations)
    ? rawInvitations.map(normalizeInvitation).filter((i): i is AdminCompanyInvitation => Boolean(i))
    : [];
  return {
    companyId,
    name: asString(c.name ?? c.Name).trim(),
    status: asString(c.status ?? c.Status ?? 'Active').trim(),
    stripeCustomerId: asOptionalString(c.stripeCustomerId ?? c.StripeCustomerId),
    createdAt: asString(c.createdAt ?? c.CreatedAt),
    suspendedAt: asOptionalString(c.suspendedAt ?? c.SuspendedAt),
    suspendedBy: asOptionalString(c.suspendedBy ?? c.SuspendedBy),
    merchants,
    pendingInvitations,
  };
}

function normalizeDirectoryRow(row: unknown): AdminCompanyDirectoryRow | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  const merchantId = asOptionalString(r.merchantId ?? r.MerchantId);
  const invitationId = asOptionalString(r.invitationId ?? r.InvitationId);
  if (!merchantId && !invitationId) {
    return null;
  }
  return {
    merchantId,
    name: asString(r.name ?? r.Name).trim(),
    email: asString(r.email ?? r.Email).trim(),
    phone: asString(r.phone ?? r.Phone).trim(),
    city: asString(r.city ?? r.City).trim(),
    state: asString(r.state ?? r.State).trim(),
    country: asString(r.country ?? r.Country).trim(),
    isActive: Boolean(r.isActive ?? r.IsActive),
    isPendingInvitation: Boolean(r.isPendingInvitation ?? r.IsPendingInvitation),
    createdAt: asString(r.createdAt ?? r.CreatedAt),
    invitationId,
    invitationExpiresAt: asOptionalString(r.invitationExpiresAt ?? r.InvitationExpiresAt),
    invitationStatus: asOptionalString(r.invitationStatus ?? r.InvitationStatus),
  };
}

/**
 * Full merchant + invitation directory for an admin-chosen company — the same rows the 3PL
 * company sees about itself, used by the admin "impersonate company" invitations view.
 */
export async function fetchAdminCompanyMerchants(
  companyId: string,
  getAccessToken: () => string | undefined
): Promise<AdminCompanyDirectoryRow[]> {
  const result = await apiFetchJson<unknown>(`/api/admin/companies/${encodeURIComponent(companyId)}/merchants`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  if (!result || typeof result !== 'object') {
    return [];
  }
  const rawRows = (result as Record<string, unknown>).merchants ?? (result as Record<string, unknown>).Merchants;
  return Array.isArray(rawRows)
    ? rawRows.map(normalizeDirectoryRow).filter((r): r is AdminCompanyDirectoryRow => Boolean(r))
    : [];
}

/**
 * A 3PL company's own login accounts (operator/admin users) — distinct from its merchants —
 * used to pick an impersonation target among the company's own staff.
 */
export async function fetchAdminCompanyUsers(
  companyId: string,
  getAccessToken: () => string | undefined
): Promise<AdminCompanyUser[]> {
  const result = await apiFetchJson<unknown>(`/api/admin/companies/${encodeURIComponent(companyId)}/users`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  return Array.isArray(result)
    ? result.map(normalizeCompanyUser).filter((u): u is AdminCompanyUser => Boolean(u))
    : [];
}

/** Emits a lightweight client-side audit signal (backend is the authoritative trail). */
export function logAdminCompanyAudit(event: 'access' | 'statusChange', details: Record<string, unknown>): void {
  const payload = { event, timestamp: new Date().toISOString(), ...details };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bo-admin-company-audit', { detail: payload }));
  }
  console.info('[AdminCompanyAudit]', payload);
}

export async function fetchAdminCompany(
  companyId: string,
  getAccessToken: () => string | undefined
): Promise<AdminCompanyDetail | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/companies/${encodeURIComponent(companyId)}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });
  return normalizeCompanyDetail(result);
}

export async function updateAdminCompanyStatus(
  companyId: string,
  status: AdminCompanyStatus,
  getAccessToken: () => string | undefined
): Promise<AdminCompanyDetail | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/companies/${encodeURIComponent(companyId)}`, {
    method: 'PATCH',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  return normalizeCompanyDetail(result);
}
